import React, { useEffect, useRef, useState } from 'react';
import { Badge, Modal, Pagination, Table, TextInput } from '@mantine/core';
import type { HouseAccountCustomerView } from '../../../../../services/house-accounts/house-accounts.service';
import { houseAccountsService } from '../../../../../services/house-accounts/house-accounts.service';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from './house-account-assignment-modal.module.scss';

const PAGE_SIZE = 20;

export type HouseAccountAssignmentModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  orderId: number;
  assignedCustomerId: number | null;
};

export const HouseAccountAssignmentModal: React.FC<HouseAccountAssignmentModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  orderId,
  assignedCustomerId,
}) => {
  const [items, setItems] = useState<HouseAccountCustomerView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(assignedCustomerId);
  const assignLockRef = useRef(false);

  useEffect(() => {
    setSelectedCustomerId(assignedCustomerId);
  }, [assignedCustomerId]);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // System aggregator accounts are settled by their integration, so the server pages them out.
    void houseAccountsService.list(tenantId, employeeSession, page, PAGE_SIZE, search, 'active', true)
      .then((result) => {
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      })
      .catch((loadError: Error) => { if (!cancelled) setError(loadError.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [employeeSession, opened, page, search, tenantId]);

  const setAssignment = async (customerId: number | null) => {
    if (assignLockRef.current) return;
    assignLockRef.current = true;
    setError(null);
    try {
      await houseAccountsService.assignToOrder(tenantId, orderId, customerId, employeeSession);
      setSelectedCustomerId(customerId);
    } catch (assignError) {
      setError((assignError as Error).message);
    } finally {
      assignLockRef.current = false;
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="שיוך חשבון בית להזמנה" size="lg" centered classNames={{ root: navyModal.root }}>
      <div className={classes.body} dir="rtl" onContextMenu={(event) => event.preventDefault()}>
        <div className={classes.searchRow}>
          <TextInput
            label="חיפוש לפי שם או טלפון"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.currentTarget.value)}
            onSelect={(event) => {
              const input = event.currentTarget;
              const end = input.value.length;
              if (input.selectionStart !== end || input.selectionEnd !== end) input.setSelectionRange(end, end);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { setPage(1); setSearch(searchDraft.trim()); }
            }}
          />
          <Button variant="confirm" size="sm" onClick={() => { setPage(1); setSearch(searchDraft.trim()); }}>חפש</Button>
        </div>
        {isLoading ? <p>טוען...</p> : (
          <Table striped highlightOnHover>
            <Table.Thead><Table.Tr><Table.Th>לקוח</Table.Th><Table.Th>חוב</Table.Th><Table.Th>מסגרת</Table.Th><Table.Th></Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {items.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.fullName}<small>{item.mobilePhone}</small></Table.Td>
                  <Table.Td>{formatMoney(item.outstandingDebt)} ₪</Table.Td>
                  <Table.Td>{item.creditLimit === null ? <Badge color="blue">ללא הגבלה</Badge> : `${formatMoney(item.availableCredit ?? 0)} ₪`}</Table.Td>
                  <Table.Td>
                    <Button variant="save" size="sm" onClick={() => void setAssignment(selectedCustomerId === item.id ? null : item.id)}>
                      {selectedCustomerId === item.id ? 'משויך' : 'שייך'}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / PAGE_SIZE))} size="sm" />
        {error && <p className={classes.error}>{error}</p>}
      </div>
    </Modal>
  );
};
